import { useEffect, useState } from "react";
import { api, money, errorMessage } from "../api";
import { Check, Crown, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import ProposedMatrix from "./ProposedMatrix";
import { SubscriptionPanel } from "./SubscriptionPanel";

const CYCLE_ORDER = ["monthly", "half_yearly", "yearly"];
const SAVE = { half_yearly: "Save 10% vs monthly", yearly: "Save 20% vs monthly" };

async function loadCheckout() {
  if (window.Razorpay) return;
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve; s.onerror = () => reject(new Error("Could not load Razorpay Checkout")); document.body.appendChild(s);
  });
}

function PlanCard({ p, data, cycle, hotel, current, busy, onPick }) {
  const prices = hotel ? p.prices_hotel || p.prices : p.prices;
  const feats = [...p.features, ...(hotel ? data.hotel_features?.[p.id] || [] : [])];
  const label = (f) => data.feature_catalog.find((x) => x.key === f)?.label || f;
  const lim = p.limits || {};
  return (
    <article className={`plan-card ${p.popular ? "popular" : ""} ${current ? "current" : ""}`} data-testid={`plan-card-${p.id}`}>
      {p.popular && <span className="plan-ribbon"><Crown size={11} /> Most popular</span>}
      <header><h3>{p.name}</h3><p>{p.tagline}</p></header>
      <div className="plan-price">
        <strong data-testid={`plan-price-${p.id}`}>{money(prices[cycle])}</strong>
        <small>{data.cycles.find((x) => x.id === cycle)?.label} · incl. 18% GST</small>
        {SAVE[cycle] && <em>{SAVE[cycle]}</em>}
      </div>
      <ul className="plan-feats">
        {feats.slice(0, 10).map((f) => <li key={f}><Check size={12} /> {label(f)}</li>)}
        {feats.length > 10 && <li className="more">+ {feats.length - 10} more</li>}
        <li className="more">Outlets <b>{lim.max_branches ?? p.max_branches}</b> · Staff <b>{lim.max_staff ?? "Unlimited"}</b>{hotel ? <> · Rooms <b>{lim.max_rooms ?? "Unlimited"}</b></> : null}</li>
      </ul>
      <button className="plan-cta" disabled={!!busy || current} onClick={() => onPick(p.id)} data-testid={`subscribe-${p.id}`}>
        {busy === p.id ? <Loader2 className="spin" size={14} /> : <Sparkles size={14} />}{current ? "Current plan" : `Choose ${p.name}`}
      </button>
    </article>
  );
}

export default function Plans({ entitlements, trialExpired, onActivated, onRefresh }) {
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [rev, setRev] = useState(0);
  const hotel = entitlements?.hotel_needed;
  const sub = entitlements?.source === "paid" ? entitlements.subscription : null;

  useEffect(() => { api.get("/plans").then((r) => setData(r.data)).catch(() => {}); }, []);

  const done = async (text) => { setMsg(text); setRev((x) => x + 1); await onActivated?.(); };
  const subscribe = async (planId) => {
    setBusy(planId); setErr(""); setMsg("");
    try {
      const { data: c } = await api.post("/subscribe/checkout", { plan_id: planId, cycle });
      if (c.scheduled) { setRev((x) => x + 1); setMsg(`Plan change scheduled — it starts at your next renewal.`); return; }
      if (c.starts_at) setMsg(`Your current paid period continues — the new plan's first charge will be on ${c.starts_at.slice(0, 10).split("-").reverse().join("-")}.`);
      if (c.mock) { await api.post("/subscribe/mock-pay", { subscription_id: c.subscription_id }); await done("Subscription activated (mock mode)."); return; }
      await loadCheckout();
      new window.Razorpay({
        key: c.key_id, subscription_id: c.subscription_id, name: c.app_name, description: `${c.plan_name} · ${c.edition_label} · ${c.cycle_label}`,
        prefill: c.prefill, theme: { color: "#c05a26" },
        handler: async (res) => {
          try {
            const { data: v } = await api.post("/subscribe/verify", { subscription_id: res.razorpay_subscription_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature });
            if (v.status === "scheduled") { setRev((x) => x + 1); setMsg(`Mandate approved — your new plan starts on ${v.starts_at.slice(0, 10).split("-").reverse().join("-")} when the current paid period ends.`); await onRefresh?.(); return; }
            await done(`Subscription active${v.invoice_no ? ` · invoice ${v.invoice_no}` : ""}.`);
          } catch (e) { setErr(errorMessage(e)); }
        },
        modal: { ondismiss: () => setBusy("") },
      }).open();
    } catch (e) { setErr(errorMessage(e)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="plans-shell" data-testid="plans-loading"><Loader2 className="spin" size={22} /> Loading plans…</div>;
  return (
    <div className="plans-shell" data-testid="plans-view">
      {trialExpired && <div className="paywall-banner" data-testid="paywall-banner"><ShieldCheck size={16} /> Your free trial has ended. Choose a plan to continue — all your data is kept.</div>}
      {entitlements?.edition_mismatch && <div className="paywall-banner" data-testid="edition-mismatch-banner"><ShieldCheck size={16} /> The Hotel module is on in one of your branches, but your plan is Restaurant only. Choose a Restaurant + Hotel plan to unlock rooms and halls.</div>}
      <SubscriptionPanel key={rev} onChanged={async (t) => { setErr(""); setMsg(t); await onRefresh?.(); }} />
      <div className="plans-head">
        <span className="overline">PRICING · {hotel ? "RESTAURANT + HOTEL" : "RESTAURANT ONLY"}</span>
        <h1>Simple, honest pricing for Indian restaurants.</h1>
        <p data-testid="plans-edition-note">Prices include 18% GST and renew automatically. {hotel ? "Restaurant + Hotel prices apply because the Hotel module is on in a branch." : "Turn on the Hotel module in a branch to see Restaurant + Hotel plans."}</p>
        <div className="cycle-toggle" role="tablist" aria-label="Billing cycle">
          {CYCLE_ORDER.map((c) => <button key={c} className={cycle === c ? "cycle on" : "cycle"} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}>{data.cycles.find((x) => x.id === c)?.label || c}</button>)}
        </div>
      </div>
      {msg && <div className="auth-alert info" data-testid="plans-message">{msg}</div>}
      {err && <div className="auth-alert error" data-testid="plans-error">{err}</div>}
      <div className="plan-grid">
        {data.plans.map((p) => <PlanCard key={p.id} p={p} data={data} cycle={cycle} hotel={hotel} busy={busy} onPick={subscribe}
          current={!!sub && sub.plan_id === p.id && sub.cycle === cycle && sub.edition === (hotel ? "hotel" : "restaurant") && sub.status === "active"} />)}
      </div>
      <ProposedMatrix />
      <footer className="plans-foot"><small>Billed by {data.legal_entity} via Razorpay. Plan changes apply from your next renewal; cancelling keeps access until the paid period ends.</small></footer>
    </div>
  );
}
